import { getServerValidatedEnv } from '../../config/server-env';
import { SanityFetcherService } from '../../services/SanityService/SanityFetcher';
import { isSSR } from '../../utils/helpers';
import { SemboMarket } from '../../utils/markets';
import {
  getCopyQuery,
  imageByRef,
  isAiEnabled,
  mediaFileById,
  mediaGroupById,
  prefooterTags,
  slugDefinedInEntriesWithLanguage,
} from './queries';
import {
  CopyQueryResult,
  GetCopyResult,
  GetPrefooterTagsResult,
  PrefooterTagsQueryResult,
  SanityClientFetchOptions,
  SanityImageType,
} from './types';

export const getCopy = async (
  type: 'microcopy' | 'macrocopy',
  filters: { include?: string[]; exclude?: string[] } | undefined,
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<GetCopyResult> => {
  // Disable translations if specified by environment settings (useful for debugging)
  if (isSSR()) {
    const env = getServerValidatedEnv();

    if (env.TRANSLATION_DISABLED) {
      return {};
    }
  }

  const query = getCopyQuery(type, filters, market);
  const result = await SanityFetcherService.fetchAndLog<CopyQueryResult>(
    query,
    previewSettings
  );

  return result.reduce((coll: GetCopyResult, item) => {
    coll[item.key] = item.value;
    return coll;
  }, {});
};

export const getSlugDefinedInEntriesWithLanguage = async (
  slug: string,
  excludeId: string,
  contentType: string,
  path: string,
  previewSettings: SanityClientFetchOptions
): Promise<boolean> => {
  const queryUrl = slugDefinedInEntriesWithLanguage(
    slug,
    excludeId,
    contentType,
    path
  );
  const result = await SanityFetcherService.fetchAndLog<boolean>(
    queryUrl,
    previewSettings
  );
  return result;
};

export const getMediaGroupById = async (
  market: SemboMarket,
  id: string,
  previewSettings: SanityClientFetchOptions
): Promise<SanityImageType[]> => {
  const queryUrl = mediaGroupById(market, id);

  const mediaGroup = await SanityFetcherService.fetchAndLog<SanityImageType[]>(
    queryUrl,
    previewSettings
  );

  return mediaGroup ?? [];
};

export const getMediaFileById = async (
  id: string,
  previewSettings: SanityClientFetchOptions
): Promise<string> => {
  const queryUrl = mediaFileById(id);

  const mediaFile = await SanityFetcherService.fetchAndLog<string>(
    queryUrl,
    previewSettings
  );

  return mediaFile;
};

export const getIsAiEnabledMarket = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<boolean> => {
  const result = await SanityFetcherService.fetchAndLog<boolean>(
    isAiEnabled(market),
    previewSettings
  );

  return result;
};

export const getImageByRef = async (
  market: SemboMarket,
  ref: string,
  previewSettings: SanityClientFetchOptions
): Promise<SanityImageType | null> => {
  const result = await SanityFetcherService.fetchAndLog<SanityImageType | null>(
    imageByRef(market, ref),
    previewSettings
  );

  return result;
};

export const getPrefooterTags = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<GetPrefooterTagsResult> => {
  const prefooterTagsResponse =
    await SanityFetcherService.fetchAndLog<PrefooterTagsQueryResult>(
      prefooterTags(market),
      previewSettings
    );

  return {
    translationKey: 'explore.categories',
    links: prefooterTagsResponse,
  };
};
